import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Handshake, ChevronLeft, ChevronRight } from "lucide-react";
import API_BASE_URL from "../config/Config";
import AmbassadorCard from "./AmbassadorCard";
import GlassCard from "./ui/GlassCard";
import { PageLoader } from "./ui/Loader";

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const partnersPerPage = 6;

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/posts/?post_type=PARTNER`)
      .then((res) => {
        const sorted = (res.data.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setPartners(sorted);
      })
      .catch((err) => {
        console.error("Ошибка при загрузке партнёров", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const totalPages = Math.ceil(partners.length / partnersPerPage);
  const paginatedPartners = partners.slice(
    (currentPage - 1) * partnersPerPage,
    currentPage * partnersPerPage
  );

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const delta = 1;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pages.push(i);
      } else if (
        i === currentPage - delta - 1 ||
        i === currentPage + delta + 1
      ) {
        pages.push("...");
      }
    }

    const uniquePages = [...new Set(pages)];

    return (
      <div className="flex justify-center items-center mt-8 flex-wrap gap-2">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-dark-500 bg-dark-50 hover:bg-dark-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Предыдущая страница"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {uniquePages.map((page, idx) =>
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-dark-400">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${
                currentPage === page
                  ? "bg-primary-500 text-white shadow-glow"
                  : "text-dark-500 bg-dark-50 hover:bg-dark-100"
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-dark-500 bg-dark-50 hover:bg-dark-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Следующая страница"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header band */}
      <div className="relative overflow-hidden bg-gradient-hero pt-32 pb-20 px-6">
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-primary-400/30 rounded-full blur-3xl animate-float-slow" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl animate-float" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-semibold tracking-wide uppercase rounded-full bg-white/15 text-white backdrop-blur-sm"
          >
            <Handshake className="w-4 h-4" />
            Партнёры
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Наши партнёры
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-white/80 max-w-xl mx-auto"
          >
            Люди, компании и организации, которые поддерживают миссию «Жасыл Желкен».
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 pb-20 relative z-10">
        <div className="mb-8">
          <AmbassadorCard />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <GlassCard hover={false} className="p-6 md:p-10 bg-white">
            {partners.length === 0 ? (
              <p className="text-center text-dark-400 py-10">Партнёров пока нет</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
                  {paginatedPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="group flex items-center justify-center rounded-xl bg-dark-50 p-4"
                    >
                      <img
                        src={partner.image_url}
                        alt={partner.title}
                        title={partner.title}
                        className="h-16 w-auto max-w-[160px] object-contain opacity-90 transition-all duration-300 group-hover:scale-105 group-hover:opacity-100 md:h-20 md:max-w-[180px]"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/220x120?text=Logo";
                        }}
                      />
                    </div>
                  ))}
                </div>

                {renderPagination()}
              </>
            )}
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnersPage;
